<template>
    <v-dialog
        :value="importDialog"
        @input="reset()"
        persistent
        scrollable
        max-width="50vw"
      >
        <v-card>
          <v-card-title class="px-4">
            <div class="title">{{$t('screener.film.import.dialog.title')}}</div>
            <v-spacer></v-spacer>
            <v-btn
              class="mx-0 px-0"
              small
              dark
              icon
              color="grey"
              @click="reset()"
            >
              <v-icon small>$vuetify.icons.rms_close</v-icon>
            </v-btn>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <v-stepper class="elevation-0" v-model="importStep">
              <v-stepper-header flat class="elevation-0 px-0">
                <template v-for="step,index in steps">
                    <v-stepper-step
                    :key="index"
                    :step="step.value"
                    :complete="importStep > step.value"
                    :editable="step.editable"
                    class="px-3"
                    >{{step.label}}
                    </v-stepper-step>
                    <v-divider v-if="index+1 < steps.length" :key="index+'div'"></v-divider>
                </template>
              </v-stepper-header>
              <v-stepper-items class="pa-3">
                <!-- Step 1 Upload File -->
                <v-stepper-content step="1" class="pa-0">
                    <ExcelFileUploader
                    :fileName.sync="fileName"
                    :file.sync="fileObject"
                    :hint="$t('screener.film.import.upload.file_hint')"
                    :mimeType="'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'"
                    @onUploadSelectedFileClick="continueWithSelectedFile"
                    ></ExcelFileUploader>
                   <!-- Upload instructions -->
                    <div class="mt-5">
                      <div class="greyDarken2--text subheading pb-1"><b>{{$t('remember.title')}}</b></div>
                      <ul class="subheading greyDarken2--text">
                        <li v-for="instruction,index in uploadInstructions" :key="index" v-html="instruction.text"></li>
                        <a :href="sampleFile.src"
                          @click.prevent="downloadSampleFile(sampleFile.src, sampleFile.title)">Download Sample File</a>
                      </ul>
                    </div>
                </v-stepper-content>
                <!-- Step 2 Map fields -->
                <v-stepper-content step="2" class="pa-0">
                  <v-card style="height:400px;overflow:scroll">
                    <v-data-table
                    :headers="headers"
                    :items="fileHeader"
                    hide-actions
                    class="border-grey-all my-2">
                    <template v-slot:headers="props">
                      <th v-for="header in props.headers" :key="header.text" :class="[`text-xs-${header.align}`]">
                        <h3 class="greyDarken2--text">{{header.text}}</h3>
                      </th>
                    </template>
                    <template v-slot:items="props">
                        <td class="body-2">{{ props.item.text }}</td>
                        <td width="30%" class="text-xs-right">
                          <v-autocomplete
                            v-model="props.item.mappedTo"
                            :items="DEFINES.FILM.INPUT_FIELDS"
                            color="primary"
                            :label="$t('screener.film.import.mew_field.input.label')"
                            item-text="label"
                            item-value="value"
                            :menu-props="{closeOnContentClick:true}"
                            clearable
                            @click:clear="removeFromDisabledFields(props.item.mappedTo)"
                          >
                            <template v-slot:item="data">
                              <v-list-tile :disabled="disabledFields.includes(data.item.value)" @click="addToDisabledFields(props.item.mappedTo,data.item.value), props.item.mappedTo = data.item.value">
                                <v-list-tile-content>
                                  <v-list-tile-title v-text="data.item.label"></v-list-tile-title>
                                </v-list-tile-content>
                              </v-list-tile>
                            </template>
                          </v-autocomplete>
                        </td>
                    </template>
                  </v-data-table>
                  </v-card>
                </v-stepper-content>
                <!-- Step 3 Update Existing Films -->
                <v-stepper-content step="3" class="pa-0">
                  <v-checkbox
                    v-model="updateExistingFilms"
                    :label="$t('screener.film.import.update_film_step.title')"
                    color="primary"
                  ></v-checkbox>
                  <div class="subheading greyDarken2--text">
                    {{$t('screener.film.import.update_film_step.content')}}
                  </div>
                </v-stepper-content>
                <!-- Step 4 Confirmation -->
                <v-stepper-content step="4" class="pa-0">
                  <ul class="subheading greyDarken2--text">
                    <li v-for="instruction,index in instructions" :key="index" v-html="instruction.text"></li>
                  </ul>
                </v-stepper-content>
              </v-stepper-items>
            </v-stepper>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions class="pa-4 mx-1">
            <v-btn
            v-if="importStep > 1"
            class="mx-0"
            dark
            color="grey"
            @click="goToStep(importStep-1)"
            ><v-icon small class="px-1"
              >$vuetify.icons.screener_back</v-icon
            >{{ $t("screener.btn.back") }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn class="ml-0" dark color="grey" @click="reset()">{{
            $t("rms.button.cancel")
          }}</v-btn>
          <v-btn
            class="mr-0"
            dark
            color="secondary"
            @click="_triggerActionForCurrentStep()">
            {{importStep == steps.length ? $t('screener.btn.import') : $t('rms.button.continue') }}</v-btn>
          </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import ExcelFileUploader from "@/components/ExcelFileUploader";
import readXlsxFile from 'read-excel-file';
import axios from "axios";

export default {
    name:"ImportFilms",
    props:{
        importDialog:{
            type:Boolean,
            default:false
        }
    },
    components:{
        ExcelFileUploader
    },
    data(){
        return{
            importStep:1,
            updateExistingFilms:false,
            steps:[
                {
                    label:this.$t('screener.film.import.steps.upload_file.title'),
                    value:1,
                    editable:false
                },
                {
                    label:this.$t('screener.film.import.steps.map_fields.title'),
                    value:2,
                    editable:false
                },
                {
                    label:this.$t('screener.film.import.steps.update_existing_film.title'),
                    value:3,
                    editable:false
                },
                {
                    label:this.$t('screener.film.import.steps.confirmation.title'),
                    value:4,
                    editable:false
                }
            ],
            fileName:null,
            fileObject:null,
            fileHeader:[],
            headers:[
              {
                text: this.$t('screener.film.import.map_fields.header.original_file'),
                value: 'file',
                align: 'left',
                sortable: false,
              },
              {
                text: this.$t('screener.film.import.map_fields.header.mew_fields'),
                value: 'mew',
                align: 'right',
                sortable: false,
              },
            ],
            instructions:[
              {text:this.$t('screener.film.import.confirmation_step.instruction_1')},
              {text:this.$t('screener.film.import.confirmation_step.instruction_2')}
            ],
            uploadInstructions:[
              {text:this.$t('screener.film.import.upload_file.instruction_1')},
              {text:this.$t('screener.film.import.upload_file.instruction_2')},
              {text:this.$t('screener.film.import.upload_file.instruction_3')},
              {text:this.$t('screener.film.import.upload_file.instruction_4')},
              {text:this.$t('screener.film.import.upload_file.instruction_5')}
            ],
            disabledFields:[],
            sampleFile :{
              title: 'Sample.xlsx',
              src: this.DEFINES.SERVER_URL+'assets/sample-films-import.xlsx',
            }
        }
    },
    watch:{
      fileObject:{
        deep:true,
        handler:function(){
          this._setFileHeader(this.fileObject);
        }
      }
    },
    methods:{
      removeFromDisabledFields(field){
        const index = this.disabledFields.indexOf(field);
        if (index > -1) {
          this.disabledFields.splice(index, 1);
        }
      },
      addToDisabledFields(oldField=null, newField){
        if(oldField){
          this.removeFromDisabledFields(oldField);
        }
        this.disabledFields.push(newField);
      },
      _setFileHeader(file){
        if(!file){
          return;
        }
        var _this = this;
        readXlsxFile(file).then((rows) => {
          let header = rows[0];
          _this.fileHeader = header.map((header)=>{
            let headerObj = {}
            headerObj.text = header;
            headerObj.value = header;
            headerObj.mappedTo = null;
            return headerObj;
          });
        });
      },
      async continueWithSelectedFile()
      {
        try {
          this._validateFile();
          this.goToStep(2);
        } catch (error) {
          this.$store.dispatch('app/showMessage', {
            message: error.message,
            color: 'error',
          });
        }
      },
      _triggerActionForCurrentStep(){
        if(!this.importStep){
          return;
        }
        switch (this.importStep) {
          case 1:
            return this.continueWithSelectedFile();
          case 2:
            return this.goToStep(3);
          case 3:
            return this.goToStep(4);
          case 4:
            return this.importFilms();
        }
      },
      _validateFile(){
          // A file is not chosen!
          if (!this.fileObject) {
            throw {message:this.$t('screener.film.import.file.validation.missing')};
          }

          // Check header is available or not
          if(!this.fileHeader || !this.fileHeader.length){
            throw {message:this.$t('screener.film.import.file.validation.header_missing')};
          }
      },
      async importFilms(){
        try {
          // Preparing payload
          var payload = new FormData();
          payload.append('file', this.fileObject);
          payload.append('fileHeader', JSON.stringify(this.fileHeader));
          payload.append('updateExistingFilms', this.updateExistingFilms);

          var response = await this.$store.dispatch('film/import',payload);
          this.$store.dispatch('app/showMessage', {
            message: this.$t('screener.film.import.success'),
            color: 'success',
          });
          this.reset();
        } catch (error) {
          this.$store.dispatch('app/showMessage', {
            message: error.message,
            color: 'error',
          });
        }
      },
      reset() {
        this.$emit('update:importDialog',false);
        this.fileName = null;
        this.fileObject = null;
        this.fileHeader = [];
        this.goToStep(1);
      },
      goToStep(step){
        this.importStep = step;
      },
      downloadSampleFile(url, title) {
        axios({
            url: 'https://'+this.DEFINES.API_DOMAIN+'/downloadSampleMediaFile',
            method: 'GET',
            responseType: 'arraybuffer',
        }).then((response) => {
              let blob = new Blob([response.data])
                  let link = document.createElement('a')
                  link.href = window.URL.createObjectURL(blob)
                  link.download = 'sample.xlsx'
                  link.click()
        }).catch(() => 
          console.log('error occured')
        );
      },
    }
}
</script>

<style scoped>
::-webkit-scrollbar {
width: 6px !important;
}
::-webkit-scrollbar-thumb {
border-radius: 6px !important;
background: lightgrey !important;
}
</style>
